import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

import { ROUTES } from '#/config/routes';
import ListTablePanel from '#/modules/commonComponents/table/ListTablePanel';
import { buildFilterStatusFieldsFromColumns } from '#/modules/commonComponents/table/buildFilterStatusFields';
import { DEFAULT_LIST_SORT_FIELD_LABELS } from '#/modules/commonComponents/table/listFilterKeys';
import AftersalesOpsPageHeader from '#/modules/crmMgmt/aftersales/components/AftersalesOpsPageHeader';
import { buildRepairItemColumns } from '#/modules/crmMgmt/aftersales/repairItem/data/repairItemColumns';
import {
  getRepairItemApiErrorMessage,
} from '#/modules/crmMgmt/aftersales/repairItem/helper/apiError';
import { applyRepairRequestDateRangeToFilters } from '#/modules/crmMgmt/aftersales/repairItem/helper/repairRequestDateListFilter';
import useRepairItemList from '#/modules/crmMgmt/aftersales/repairItem/hooks/useRepairItemList';
import PageShell from '#/modules/infra/layout/PageShell';

const SORT_FIELD_LABELS = {
  ...DEFAULT_LIST_SORT_FIELD_LABELS,
  repairRequestNo: '報修單號',
  repairRequestDate: '報修日期',
  status: '狀態',
  serialNo: '序號',
  productCode: '產品代號',
  itemName: '品名',
  createdAt: '建立時間',
};

const RepairItemListPage = () => {
  const navigate = useNavigate();
  const {
    rows,
    pagination,
    isLoading,
    isError,
    error,
    refetch,
    listQuery,
    setPage,
    setPageSize,
    setKeyword,
    setFilter,
    setSort,
    removeSortAt,
    resetListQuery,
    setListQuery,
  } = useRepairItemList();

  const setRepairRequestDateRangeFilter = useCallback((range) => {
    setListQuery((prev) => ({
      ...prev,
      page: 1,
      filters: applyRepairRequestDateRangeToFilters(prev.filters, range),
    }));
  }, [setListQuery]);

  const handleFilterChange = useCallback((key, value) => {
    if (key === 'repairRequestDateFrom' && value === undefined) {
      setRepairRequestDateRangeFilter(undefined);
      return;
    }
    setFilter(key, value);
  }, [setFilter, setRepairRequestDateRangeFilter]);

  const columns = useMemo(
    () => buildRepairItemColumns({
      filters: listQuery.filters || {},
      onFilterChange: handleFilterChange,
      onRepairRequestDateRangeChange: setRepairRequestDateRangeFilter,
    }),
    [listQuery.filters, handleFilterChange, setRepairRequestDateRangeFilter],
  );

  const filterStatusFields = useMemo(
    () => buildFilterStatusFieldsFromColumns(columns, { filters: listQuery.filters }),
    [columns, listQuery.filters],
  );

  const handleSortingChange = useCallback((sorting) => {
    setSort((sorting ?? []).map((item) => ({
      id: item.id,
      desc: Boolean(item.desc),
    })));
  }, [setSort]);

  const handleRowDoubleClick = useCallback((row) => {
    const id = row.original?.id;
    if (!id) {
      return;
    }
    navigate(ROUTES.repairItemDetail(id));
  }, [navigate]);

  return (
    <PageShell variant="listWithSide">
      <div className="layout-shell__body flex min-h-0 flex-col">
        <AftersalesOpsPageHeader page="repairItem" variant="list" />
        <div className="table-panel min-h-0 xl:h-full">
          <ListTablePanel
            columns={columns}
            rows={rows}
            pagination={pagination}
            isLoading={isLoading}
            isError={isError}
            errorMessage={
              isError
                ? getRepairItemApiErrorMessage(error, '載入列表失敗')
                : undefined
            }
            onRetry={isError ? () => { refetch(); } : undefined}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            onRowDoubleClick={handleRowDoubleClick}
            onSearch={setKeyword}
            onReset={resetListQuery}
            initialKeyword={listQuery.keyword}
            searchPlaceholder="搜尋單號、序號或品名"
            listQuery={listQuery}
            filterStatusFields={filterStatusFields}
            sortFieldLabels={SORT_FIELD_LABELS}
            onFilterChange={handleFilterChange}
            onKeywordClear={() => setKeyword('')}
            onSortRemove={removeSortAt}
            onSortingChange={handleSortingChange}
          />
        </div>
      </div>
    </PageShell>
  );
};

export default RepairItemListPage;
